export type LogoVariant = "horizontal" | "stacked" | "wordmark" | "logomark";
export type LogoTone = "black" | "green" | "white";

export interface LogoProps {
  /** Visual shape — defaults to "logomark" (square mark only). */
  variant?: LogoVariant;
  /** Color of the rendered SVG. Defaults to "green" (brand). */
  tone?: LogoTone;
  /** CSS height — pixels by default. Aspect ratio is preserved. Defaults to 32. */
  size?: number | string;
  /** Override the alt text. Pass empty string for decorative use. Defaults to "Storemandu". */
  alt?: string;
  className?: string;
}

const VARIANT_PATH: Record<LogoVariant, string> = {
  horizontal: "HorizontalLogo",
  stacked: "StackedLogo",
  wordmark: "WordMark",
  logomark: "LogoMark",
};

const TONE_PATH: Record<LogoTone, string> = {
  black: "Black",
  green: "Green",
  white: "White",
};

// Intrinsic width/height ratio per variant, read from each SVG's viewBox
// (all three tones share a viewBox). Used to emit width/height attributes +
// CSS aspect-ratio so the browser reserves the box before the SVG arrives
// (Lighthouse "image elements do not have explicit width and height" / CLS).
const VARIANT_RATIO: Record<LogoVariant, number> = {
  horizontal: 555.53 / 117.37,
  stacked: 337.76 / 182.26,
  wordmark: 580.84 / 64.57,
  logomark: 211.42 / 275.67,
};

/**
 * Renders one of 12 staged Storemandu brand SVGs.
 *
 * Usage:
 *   <Logo />                                      32px green logomark
 *   <Logo variant="horizontal" tone="white" />    white-on-dark hero
 *   <Logo variant="wordmark" tone="black" />      print / PDF
 *
 * The SVG files live under `public/logos/` and are served same-origin.
 */
export default function Logo({
  variant = "logomark",
  tone = "green",
  size = 32,
  alt = "Storemandu",
  className,
}: LogoProps) {
  const src = `/logos/${VARIANT_PATH[variant]}_${TONE_PATH[tone]}.svg`;
  const heightStyle = typeof size === "number" ? `${size}px` : size;
  const ratio = VARIANT_RATIO[variant];
  // Note: do NOT set `display` inline. Callers use Tailwind classes like
  // `hidden` or `dark:hidden` to toggle visibility (e.g. the sidebar swaps
  // green/white tones by theme), and an inline `display` value would always
  // win over those classes and break the swap.
  return (
    <img
      src={src}
      alt={alt}
      className={className}
      width={typeof size === "number" ? Math.round(size * ratio) : undefined}
      height={typeof size === "number" ? size : undefined}
      style={{ height: heightStyle, width: "auto", aspectRatio: `${ratio}` }}
    />
  );
}
